import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { CobrandProvider } from '@/lib/use-cobrand';
import { LoginPage } from '@/components/login-page';
import { AppShell } from '@/components/app-shell';
import { IntakeSubmitPage, PublicIntakePage } from '@/pages/intake-submit';
import { ApprovalsPage } from '@/pages/approvals';
import { ApprovalDetailPage } from '@/pages/approval-detail';
import { HistoryPage } from '@/pages/history';
import { ConfirmedPage } from '@/pages/confirmed';
import { CaseDetailPage } from '@/pages/case-detail';
import { IntakePage } from '@/pages/intake';
import {
  SettingsPage,
  DoctorsSettingsPage,
  DepartmentsSettingsPage,
  NotificationsSettingsPage,
  PasswordSettingsPage,
} from '@/pages/settings';
import { RoutingSettingsPage } from '@/pages/settings-routing';
import { TeamSettingsPage } from '@/pages/settings-team';
import { IntakeSettingsPage } from '@/pages/settings-intake';

function AppRoutes() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Intake coordinators prepare and route case packets; they do not make
  // clinical approval decisions or administer the tenant portal.
  if (user?.role === 'intake_coordinator') {
    return (
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/intake" element={<IntakePage />} />
          <Route path="*" element={<Navigate to="/intake" replace />} />
        </Route>
      </Routes>
    );
  }

  // Route surface follows the backend authority model: Confirmed/History and
  // tenant administration (doctors, departments, team, intake config) are
  // rep-only; department-scoped roles work their approval queue; coordinators
  // add the intake desk.
  const isRep = user?.role === 'hospital_rep';
  const isCoordinator = user?.role === 'coordinator';

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/approvals/:id" element={<ApprovalDetailPage />} />
        {(isRep || isCoordinator) && <Route path="/intake" element={<IntakePage />} />}
        {isRep && (
          <>
            <Route path="/confirmed" element={<ConfirmedPage />} />
            <Route path="/confirmed/:id" element={<CaseDetailPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/history/:id" element={<CaseDetailPage />} />
            <Route path="/settings/doctors" element={<DoctorsSettingsPage />} />
            <Route path="/settings/departments" element={<DepartmentsSettingsPage />} />
            <Route path="/settings/routing" element={<RoutingSettingsPage />} />
            <Route path="/settings/team" element={<TeamSettingsPage />} />
            <Route path="/settings/intake" element={<IntakeSettingsPage />} />
          </>
        )}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/notifications" element={<NotificationsSettingsPage />} />
        <Route path="/settings/password" element={<PasswordSettingsPage />} />
        <Route path="*" element={<Navigate to="/approvals" replace />} />
      </Route>
    </Routes>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <img src={`${import.meta.env.BASE_URL}logo.svg`} alt="RefDash" className="w-14 h-14 rounded-2xl animate-scale-in" />
        <div className="flex gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Apply + expose the tenant co-brand skin app-wide, before auth so the
          login screen is themed too. No-op on the main domain. */}
      <CobrandProvider>
        <BrowserRouter basename="/rep">
          <Routes>
            {/* Public, token-gated patient intake — outside the auth gate so a
                patient with a link never hits the staff login (plan 021 E3). */}
            <Route path="/intake/submit/:token" element={<IntakeSubmitPage />} />
            <Route path="/intake/public/:hospitalId" element={<PublicIntakePage />} />
            {/* Everything else is the authenticated staff portal. */}
            <Route
              path="/*"
              element={
                <AuthProvider>
                  <AppRoutes />
                </AuthProvider>
              }
            />
          </Routes>
        </BrowserRouter>
      </CobrandProvider>
    </QueryClientProvider>
  );
}
